import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  LinearProgress,
  useTheme,
  Paper,
} from '@mui/material';
import {
  Person as UserIcon,
  AutoAwesome as AIIcon,
  Source as SourceIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

interface DemoMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: any[];
}

interface DemoChatMessagesProps {
  messages: DemoMessage[];
  isLoading: boolean;
  useKnowledgeBase: boolean;
}

const DemoChatMessages: React.FC<DemoChatMessagesProps> = ({ messages, isLoading, useKnowledgeBase }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const renderMessage = (message: DemoMessage) => {
    const isUser = message.role === 'user';

    return (
      <Box
        key={message.id}
        className={`message ${message.role} message-appear`}
        sx={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          gap: 2,
          mb: 3,
          px: 2,
          animation: 'fadeIn 0.3s ease-in',
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to: { opacity: 1, transform: 'translateY(0)' }
          }
        }}
      >
        {/* Avatar */}
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: isUser ? theme.palette.primary.main : theme.palette.secondary.main,
            flexShrink: 0
          }}
        >
          {isUser ? <UserIcon sx={{ fontSize: 20 }} /> : <AIIcon sx={{ fontSize: 20 }} />}
        </Avatar>

        {/* Message Content */}
        <Box sx={{ flex: 1, maxWidth: '70%' }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: isUser 
                ? theme.palette.primary.main 
                : theme.palette.background.paper,
              color: isUser ? 'white' : theme.palette.text.primary,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: isUser ? 'none' : `1px solid ${theme.palette.divider}`,
            }}
          >
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 1, 
                      '&:last-child': { mb: 0 },
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {children}
                  </Typography>
                ),
                ul: ({ children }) => (
                  <Box component="ul" sx={{ pl: 2, my: 1 }}>
                    {children}
                  </Box>
                ),
                ol: ({ children }) => (
                  <Box component="ol" sx={{ pl: 2, my: 1 }}>
                    {children}
                  </Box>
                ),
                li: ({ children }) => (
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    {children}
                  </Typography>
                ),
                code: ({ children }) => (
                  <Box
                    component="code"
                    sx={{
                      bgcolor: isUser ? 'rgba(255,255,255,0.2)' : theme.palette.action.hover,
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: '0.9em',
                      fontFamily: 'monospace'
                    }}
                  >
                    {children}
                  </Box>
                ),
                strong: ({ children }) => (
                  <Box component="strong" sx={{ fontWeight: 700 }}>
                    {children}
                  </Box>
                )
              }}
            >
              {message.content}
            </ReactMarkdown>
          </Box>

          {/* Sources */}
          {!isUser && message.sources && message.sources.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SourceIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                  Sources from Knowledge Base
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {message.sources.slice(0, 3).map((source, idx) => (
                  <Paper
                    key={idx}
                    sx={{
                      p: 1,
                      bgcolor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      maxWidth: 200
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                      {source.title || 'Document'}
                    </Typography>
                    {source.snippet && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          color: theme.palette.text.secondary,
                          display: 'block',
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {source.snippet}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* Timestamp */}
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block',
              mt: 0.5,
              color: theme.palette.text.disabled,
              fontSize: '0.7rem'
            }}
          >
            {message.timestamp.toLocaleTimeString()}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        py: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {messages.map(renderMessage)}

      {isLoading && (
        <Box sx={{ px: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: theme.palette.secondary.main,
                flexShrink: 0
              }}
            >
              <AIIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box sx={{ flex: 1, maxWidth: '70%' }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                  {useKnowledgeBase ? 'Searching knowledge base...' : 'Thinking...'}
                </Typography>
                <LinearProgress />
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <div ref={messagesEndRef} />
    </Box>
  );
};

export default DemoChatMessages;
